import { FunctionComponent } from 'react';
import LogoHeader from '../../../../assets/logo-color.png';
import WomanAnalyzingData from '../../../../assets/woman-analyzing-data.png';
import MilitaryBadge from '../../../../assets/military-trusted-business-badge.svg';
import InfoCard from 'src/components/website/MilitaryLanding/InfoCard';
import Footer from 'src/components/website/Footer';
import { SurveyResponse } from 'src/components/website/MilitaryLanding/SurveyPopup';
import {
  MilitaryConsultant,
  CERA_INFO,
  MAP_INFO,
  MILITARY_PAGE_COPY,
  MILITARY_PAGE_STEPS,
  MILITARY_PAGE_FEATURES,
  HERO_TRUST_PILLS,
  PRICING_URL,
} from 'src/constants/militaryLanding';

interface MilitaryLandingContentProps {
  readonly consultant: MilitaryConsultant;
  readonly visitorName: string;
  readonly surveyComplete: boolean;
  readonly surveyTriggered: boolean;
  readonly surveyResponses: ReadonlyArray<SurveyResponse>;
  readonly onConnectClick: () => void;
}

const formatPhone = (phone: string): string => {
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
};

const buildEmailBody = (
  visitorName: string,
  responses: ReadonlyArray<SurveyResponse>
): string => {
  if (!responses.length) return 'Military inquiry';
  const lines = responses.map((r) => `${r.questionId}: ${r.answer}`);
  return `Military inquiry\n\nVisitor name: ${visitorName}\n\nResponses:\n${lines.join('\n')}`;
};

const MilitaryLandingContent: FunctionComponent<MilitaryLandingContentProps> = ({
  consultant,
  visitorName,
  surveyComplete,
  surveyTriggered,
  surveyResponses,
  onConnectClick,
}) => {
  const callHref = `tel:${consultant.phone}`;
  const textHref = `sms:${consultant.phone}`;
  const emailHref = `mailto:${consultant.email}?subject=Military Inquiry&body=${encodeURIComponent(
    buildEmailBody(visitorName, surveyResponses)
  )}`;

  return (
    <div className="min-h-screen bg-white w-full pb-24">

      <section className="bg-primary px-5 pt-6 pb-8">
        <div className="flex items-center justify-between mb-4">
          <img
            src={LogoHeader}
            alt="ClientVitals"
            className="h-48 w-auto"
          />
          <img
            src={MilitaryBadge}
            alt="Military Trusted Business"
            className="h-32 w-auto"
          />
        </div>
        <div className="border-t border-secondary/40 pt-5 text-center">
          <h1 className="text-2xl font-medium text-white leading-snug mb-3 max-w-xs mx-auto">
            {MILITARY_PAGE_COPY.heroHeadline}
          </h1>
          <p className="text-sm text-white/85 mb-6 max-w-xs mx-auto">
            {MILITARY_PAGE_COPY.heroSubheadline}
          </p>
          <div className="flex flex-wrap justify-center gap-1.5">
            {HERO_TRUST_PILLS.map((pill) => (
              <span
                key={pill}
                className="bg-secondary/20 border border-secondary/40 rounded-full px-2.5 py-1 text-xs text-white/90"
              >
                {pill}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="px-5 pt-4">
        <div className="bg-amber-50 border-l-4 border-secondary rounded-r-lg px-4 py-4">
          <p className="text-sm text-amber-900 leading-relaxed">
            <span className="font-bold text-amber-900">
              {MILITARY_PAGE_COPY.urgencyBoldTerms}
            </span>{' '}
            {MILITARY_PAGE_COPY.urgencyMessage}
          </p>
        </div>
      </div>

      <section className="px-5 py-6 border-b border-gray-100">
        <p className="text-base font-semibold text-gray-900 tracking-wide uppercase mb-1">
          What is ClientVitals
        </p>
        <h2 className="text-sm font-medium text-primary mb-3">
          {MILITARY_PAGE_COPY.whatWeAreHeadline}
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-3">
          {MILITARY_PAGE_COPY.whatWeAreBody}
        </p>
        <p className="text-sm text-gray-500 leading-relaxed">
          {MILITARY_PAGE_COPY.ceraIntro}{' '}
          <InfoCard term="CERA" title={CERA_INFO.title} body={CERA_INFO.body} />
          {MILITARY_PAGE_COPY.ceraMapBridge}{' '}
          <InfoCard term="MAP report" title={MAP_INFO.title} body={MAP_INFO.body} />{' '}
          {MILITARY_PAGE_COPY.ceraMapOutro}
        </p>
        <span className="inline-flex items-center gap-1.5 bg-green-50 rounded-full px-3 py-1 text-xs text-green-800 mt-3">
          {MILITARY_PAGE_COPY.softPullBadge}
        </span>
      </section>

      <section className="px-5 py-6 border-b border-gray-100">
        <p className="text-base font-semibold text-gray-900 tracking-wide uppercase mb-1">
          The value
        </p>
        <h2 className="text-sm font-medium text-primary mb-3">
          {MILITARY_PAGE_COPY.valueHeadline}
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-4">
          {MILITARY_PAGE_COPY.valueBody}
        </p>

        <div className="bg-primary-light rounded-xl p-4 mb-4">
          <div className="flex justify-between items-start flex-wrap gap-3 mb-3">
            <div>
              <p className="text-3xl font-medium text-primary-dark">
                $5<span className="text-sm font-normal">/mo</span>
              </p>
              <p className="text-xs text-primary mt-0.5">{MILITARY_PAGE_COPY.bronzeLabel}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-medium text-primary-dark">$125</p>
              <p className="text-xs text-primary mt-0.5">{MILITARY_PAGE_COPY.bronzeValue}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white rounded-lg px-3 py-2.5 border border-gray-100">
              <p className="text-base font-medium text-primary">{MILITARY_PAGE_COPY.ficoLabel}</p>
              <p className="text-xs text-gray-500 mt-0.5">{MILITARY_PAGE_COPY.ficoSubLabel}</p>
            </div>
            <div className="bg-white rounded-lg px-3 py-2.5 border border-gray-100">
              <p className="text-base font-medium text-primary">{MILITARY_PAGE_COPY.roiLabel}</p>
              <p className="text-xs text-gray-500 mt-0.5">{MILITARY_PAGE_COPY.roiSubLabel}</p>
            </div>
          </div>
        </div>

        <div className="bg-secondary/10 border border-secondary/30 rounded-xl p-4 mb-4">
          <p className="text-xs font-semibold text-secondary uppercase tracking-wide mb-2">
            {MILITARY_PAGE_COPY.goldPlanLabel}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            {MILITARY_PAGE_COPY.goldValueNote}
          </p>
        </div>

        <a
          href={PRICING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-3 border-2 border-primary text-primary text-sm font-medium text-center rounded-lg hover:bg-primary hover:text-white transition-colors duration-200"
        >
          {MILITARY_PAGE_COPY.viewAllPlans}
        </a>
      </section>

      <section className="bg-primary px-5 py-8 border-b border-gray-100">
        <p className="text-base font-semibold text-white tracking-wide uppercase mb-1">
          How it works
        </p>
        <h2 className="text-sm font-medium text-white/70 mb-6">
          {MILITARY_PAGE_COPY.howItWorksHeadline}
        </h2>
        <div className="flex flex-col gap-4">
          {MILITARY_PAGE_STEPS.map((step) => (
            <div key={step.number} className="bg-white rounded-xl p-4 flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-primary text-white text-lg font-bold flex items-center justify-center flex-shrink-0">
                {step.number}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{step.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 py-6 border-b border-gray-100">
        <p className="text-base font-semibold text-gray-900 tracking-wide uppercase mb-1">
          Why ClientVitals
        </p>
        <h2 className="text-sm font-medium text-primary mb-4">
          {MILITARY_PAGE_COPY.whyUsHeadline}
        </h2>
        {MILITARY_PAGE_FEATURES.map((feature) => (
          <div key={feature} className="flex gap-2.5 mb-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
            <p className="text-sm text-gray-500">{feature}</p>
          </div>
        ))}
      </section>

      <section className="px-5 py-6 border-b border-gray-100 relative">

        {surveyTriggered && !surveyComplete && (
          <div className="absolute inset-0 bg-white/70 rounded-b-lg z-10" />
        )}

        {!surveyComplete ? (
          <div className="text-center py-4">
            <div className="mb-4">
              <img
                src={WomanAnalyzingData}
                alt="Consultant reviewing credit data"
                className="w-full h-40 object-cover rounded-lg"
              />
            </div>
            <p className="text-base font-semibold text-gray-900 tracking-wide uppercase mb-1">
              Your consultant
            </p>
            <h2 className="text-sm font-medium text-primary mb-2">
              {MILITARY_PAGE_COPY.connectHeadline}
            </h2>
            <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
              {MILITARY_PAGE_COPY.connectBody}
            </p>
            <button
              onClick={onConnectClick}
              className="w-full py-3.5 bg-secondary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity duration-200"
            >
              {MILITARY_PAGE_COPY.connectButtonLabel}
            </button>
          </div>
        ) : (
          <>
            <p className="text-base font-semibold text-gray-900 tracking-wide uppercase mb-1">
              Your consultant
            </p>
            <h2 className="text-sm font-medium text-primary mb-1.5">
              {visitorName
                ? `Hi ${visitorName}, meet ${consultant.name}`
                : MILITARY_PAGE_COPY.consultantHeadline}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {MILITARY_PAGE_COPY.consultantSubheadline}
            </p>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex gap-3 items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-light text-primary text-sm font-medium flex items-center justify-center flex-shrink-0">
                  {consultant.initials}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{consultant.name}</p>
                  <p className="text-xs text-gray-500">{consultant.role}</p>
                  <p className="text-xs text-gray-400">
                    {MILITARY_PAGE_COPY.consultantAvailability}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <a
                  href={callHref}
                  className="block py-2 border border-gray-200 rounded-lg text-xs text-center text-primary bg-white"
                >
                  <span className="block font-medium">{MILITARY_PAGE_COPY.callLabel}</span>
                  <span className="block text-gray-400 text-[10px] mt-0.5">
                    {formatPhone(consultant.phone)}
                  </span>
                </a>
                <a
                  href={textHref}
                  className="block py-2 border border-gray-200 rounded-lg text-xs text-center text-primary bg-white"
                >
                  <span className="block font-medium">{MILITARY_PAGE_COPY.textLabel}</span>
                  <span className="block text-gray-400 text-[10px] mt-0.5">
                    {formatPhone(consultant.phone)}
                  </span>
                </a>
                <a
                  href={emailHref}
                  className="block py-2 border border-gray-200 rounded-lg text-xs text-center text-primary bg-white"
                >
                  <span className="block font-medium">{MILITARY_PAGE_COPY.emailLabel}</span>
                  <span className="block text-gray-400 text-[10px] mt-0.5 break-all">
                    {consultant.email}
                  </span>
                </a>
              </div>
            </div>
          </>
        )}
      </section>

      <div className="fixed bottom-0 left-0 right-0 px-5 pb-5 pt-3 bg-white/90 backdrop-blur-sm border-t border-gray-100 z-20">
        {!surveyComplete ? (
          <button
            onClick={onConnectClick}
            className="w-full py-3.5 bg-secondary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity duration-200"
          >
            {MILITARY_PAGE_COPY.connectButtonLabel}
          </button>
        ) : (
          <a
            href={callHref}
            className="block w-full py-3.5 bg-secondary text-white rounded-lg text-sm font-medium text-center hover:opacity-90 transition-opacity duration-200"
          >
            Call {consultant.name}
          </a>
        )}
      </div>

    </div>
  );
};

export default MilitaryLandingContent;

